import logger from './logger';
import { getApiUrl, TIMEOUTS } from './config';

const apiUrl = () => getApiUrl();

export interface AuthUser {
  id: string;
  email: string;
  full_name: string | null;
}

export interface AuthSession {
  user: AuthUser;
  access_token: string;
  expires_at: number;
}

export interface UserProfile {
  id: string;
  full_name: string;
  employee_id: string | null;
  department: string | null;
  phone: string | null;
}

export interface UserRole {
  role: string;
}

// ========================================
// DEMO MODE - Usuarios para presentación
// ========================================
const DEMO_USERS = [
  { id: 'demo-admin-001', email: 'admin@madelmold.net', password: 'demo123', full_name: 'Administrador Demo', role: 'admin' },
  { id: 'demo-supervisor-001', email: 'supervisor@madelmold.net', password: 'demo123', full_name: 'Supervisor Demo', role: 'supervisor' },
  { id: 'demo-operador-001', email: 'operador@madelmold.net', password: 'demo123', full_name: 'Operador Demo', role: 'operator' },
  { id: 'demo-calidad-001', email: 'calidad@madelmold.net', password: 'demo123', full_name: 'Control de Calidad', role: 'supervisor' },
];

const isDemoMode = (): boolean => true;

const findDemoUser = (email: string, password: string) =>
  DEMO_USERS.find(u => u.email.toLowerCase() === email.toLowerCase() && u.password === password);

const getDemoUserById = (userId: string) => DEMO_USERS.find(u => u.id === userId);

class AuthService {
  private readonly SESSION_KEY = 'athenea_session';
  private readonly TOKEN_EXPIRY = 24 * 60 * 60 * 1000;

  getSession(): AuthSession | null {
    const sessionData = localStorage.getItem(this.SESSION_KEY);
    if (!sessionData) return null;
    try {
      const session: AuthSession = JSON.parse(sessionData);
      if (session.expires_at < Date.now()) {
        this.clearSession();
        return null;
      }
      return session;
    } catch {
      this.clearSession();
      return null;
    }
  }

  private setSession(user: AuthUser): AuthSession {
    const session: AuthSession = {
      user,
      access_token: this.generateToken(),
      expires_at: Date.now() + this.TOKEN_EXPIRY,
    };
    localStorage.setItem(this.SESSION_KEY, JSON.stringify(session));
    return session;
  }

  clearSession(): void {
    localStorage.removeItem(this.SESSION_KEY);
  }

  private generateToken(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  async signIn(email: string, password: string): Promise<{ session: AuthSession | null; error: any }> {
    // DEMO MODE: Check hardcoded users
    if (isDemoMode()) {
      const demoUser = findDemoUser(email, password);
      if (demoUser) {
        const user: AuthUser = {
          id: demoUser.id,
          email: demoUser.email,
          full_name: demoUser.full_name,
        };
        const session = this.setSession(user);
        logger.info('Demo mode: logged in as', demoUser.email);
        return { session, error: null };
      }
      return {
        session: null,
        error: { message: 'Usuario o contraseña incorrectos' },
      };
    }

    // Backend mode
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), TIMEOUTS.loginRequest);

      const userResponse = await fetch(
        `${apiUrl()}/users?email=eq.${encodeURIComponent(email)}&select=id,email,full_name`,
        {
          headers: { 'Accept': 'application/json' },
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      if (!userResponse.ok) {
        throw new Error('Error al buscar usuario');
      }

      const users = await userResponse.json();

      if (!users || users.length === 0) {
        return {
          session: null,
          error: { message: 'Usuario o contraseña incorrectos' },
        };
      }

      const userData = users[0];

      const verifyResponse = await fetch(`${apiUrl()}/rpc/verify_password`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userData.id,
          password: password,
        }),
      });

      if (!verifyResponse.ok) {
        throw new Error('Error al verificar contraseña');
      }

      const passwordValid = await verifyResponse.json();

      if (!passwordValid) {
        return {
          session: null,
          error: { message: 'Usuario o contraseña incorrectos' },
        };
      }

      const user: AuthUser = {
        id: userData.id,
        email: userData.email,
        full_name: userData.full_name,
      };

      const session = this.setSession(user);
      return { session, error: null };
    } catch (error: any) {
      logger.error('Sign in error:', error);
      return {
        session: null,
        error: { message: error.message || 'Error al iniciar sesión' },
      };
    }
  }

  async signOut(): Promise<void> {
    this.clearSession();
  }

  async getProfile(userId: string): Promise<{ data: UserProfile | null; error: any }> {
    // Demo mode: return mock profile
    if (isDemoMode()) {
      const demoUser = getDemoUserById(userId);
      if (demoUser) {
        return {
          data: {
            id: demoUser.id,
            full_name: demoUser.full_name,
            employee_id: 'DEMO-001',
            department: 'Producción',
            phone: '918 833 171',
          },
          error: null,
        };
      }
    }

    try {
      const response = await fetch(
        `${apiUrl()}/profiles?id=eq.${userId}&select=*`,
        { headers: { 'Accept': 'application/json' } }
      );

      if (!response.ok) throw new Error('Error al obtener perfil');

      const profiles = await response.json();
      return { data: profiles && profiles.length > 0 ? profiles[0] : null, error: null };
    } catch (error: any) {
      logger.error('Get profile error:', error);
      return { data: null, error };
    }
  }

  async getUserRole(userId: string): Promise<{ data: string | null; error: any }> {
    // Demo mode: return mock role
    if (isDemoMode()) {
      const demoUser = getDemoUserById(userId);
      if (demoUser) {
        return { data: demoUser.role, error: null };
      }
    }

    try {
      const response = await fetch(
        `${apiUrl()}/user_roles?user_id=eq.${userId}&select=role`,
        { headers: { 'Accept': 'application/json' } }
      );

      if (!response.ok) throw new Error('Error al obtener rol');

      const roles = await response.json();
      return { data: roles && roles.length > 0 ? roles[0].role : 'operator', error: null };
    } catch (error: any) {
      logger.error('Get user role error:', error);
      return { data: 'operator', error };
    }
  }

  onAuthStateChange(callback: (session: AuthSession | null) => void): () => void {
    callback(this.getSession());
    return () => {};
  }
}

export const authService = new AuthService();
